export const HEADER_MAP = {
  "QUESTION NUMBER": "question_number",
  ANSWER: "correct_answer",
  CHOICES: "answer_choices",
  CATEGORY: "category",
  VIDEO: "video",
  "PRIMARY - QUESTION IMAGE": "primary_image",
  "SUPPLEMENTAL QUESTION IMAGE 1": "supplemental_image_1",
  "SUPPLEMENTAL QUESTION IMAGE 2": "supplemental_image_2",
  "FIELD TEST QUESTION?": "is_field_test_question",
};

export const EXPECTED_CSV_HEADERS =
  "Question Number, Answer, Choices, Category, Video, Primary - Question Image, Supplemental Question Image 1, Supplemental Question Image 2, Field Test Question?";

export const VALID_CHOICE_LETTERS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "J",
  "K",
];

export const IMAGE_UPLOAD_CHUNK_SIZE = 40;

const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|bmp|tiff?|heic)$/i;

export function normalizeHeader(key) {
  return String(key ?? "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toUpperCase();
}

export function mapCsvRow(rawRow) {
  const mapped = {};
  Object.entries(rawRow || {}).forEach(([key, value]) => {
    const field = HEADER_MAP[normalizeHeader(key)];
    if (!field) return;
    mapped[field] = typeof value === "string" ? value.trim() : value;
  });
  return mapped;
}

export function defaultChoicesFromAnswer(answer) {
  const letter = String(answer || "")
    .trim()
    .toUpperCase();
  if (["A", "B", "C", "D"].includes(letter)) {
    return ["A", "B", "C", "D"];
  }
  if (["F", "G", "H", "J"].includes(letter)) {
    return ["F", "G", "H", "J"];
  }
  return null;
}

export function parseChoices(value) {
  if (value == null || String(value).trim() === "") {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map((choice) => String(choice).trim().toUpperCase())
      .filter(Boolean);
  }
  return String(value)
    .split(",")
    .map((choice) => choice.trim().toUpperCase())
    .filter(Boolean);
}

export function parseFieldTest(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return ["true", "yes", "1", "y"].includes(normalized);
}

export function normalizeFilename(name) {
  if (!name) return "";
  return String(name)
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .trim()
    .toLowerCase();
}

export function isImageFile(file) {
  const name = file?.name || "";
  return IMAGE_FILE_PATTERN.test(name);
}

export function isZipFile(file) {
  const name = String(file?.name || "").toLowerCase();
  return name.endsWith(".zip") || file?.type === "application/zip";
}

export function formatChoices(choices) {
  return (choices || []).join(", ");
}

export function validateImportQuestion(question, allQuestions = []) {
  const errors = {};
  const questionNum = parseInt(question.question_number, 10);
  if (Number.isNaN(questionNum) || questionNum <= 0) {
    errors.question_number = ["Question number must be a positive integer"];
  } else {
    const duplicateCount = allQuestions.filter(
      (row) =>
        parseInt(row.question_number, 10) === questionNum &&
        row.id !== question.id
    ).length;
    if (duplicateCount > 0) {
      errors.question_number = [
        `Question number ${questionNum} is duplicated (appears ${
          duplicateCount + 1
        } times)`,
      ];
    }
  }

  const answer = String(question.correct_answer || "")
    .trim()
    .toUpperCase();
  if (!answer) {
    errors.correct_answer = ["Answer is required"];
  } else if (!VALID_CHOICE_LETTERS.includes(answer)) {
    errors.correct_answer = [
      `Invalid answer choice. Must be one of: ${VALID_CHOICE_LETTERS.join(
        ", "
      )}`,
    ];
  }

  const choices = parseChoices(question.answer_choices);
  if (!choices.length) {
    errors.answer_choices = [
      answer === "E" || answer === "K"
        ? `Answer "${answer}" needs an explicit Choices list (default four-option lists do not include it).`
        : "Choices could not be derived from the answer. Add a Choices list.",
    ];
  } else if (choices.length < 2 || choices.length > 5) {
    errors.answer_choices = ["Choices must have 2–5 letters"];
  } else {
    const invalid = choices.filter(
      (choice) => !VALID_CHOICE_LETTERS.includes(choice)
    );
    if (invalid.length) {
      errors.answer_choices = [
        `Invalid choice letter(s): ${invalid.join(", ")}. I is not allowed.`,
      ];
    } else if (answer && !choices.includes(answer)) {
      errors.correct_answer = [
        ...(errors.correct_answer || []),
        `Answer "${answer}" is not in Choices (${formatChoices(choices)})`,
      ];
    }
  }

  return errors;
}

export function buildImageMap(questions) {
  const imageMap = [];
  questions.forEach((row) => {
    const questionNumber = parseInt(row.question_number, 10);
    if (Number.isNaN(questionNumber)) return;
    if (row.primary_image) {
      imageMap.push({
        question_number: questionNumber,
        filename: normalizeFilename(row.primary_image) || row.primary_image,
        image_type: "Primary",
      });
    }
    if (row.supplemental_image_1) {
      imageMap.push({
        question_number: questionNumber,
        filename:
          normalizeFilename(row.supplemental_image_1) ||
          row.supplemental_image_1,
        image_type: "Contextual",
      });
    }
    if (row.supplemental_image_2) {
      imageMap.push({
        question_number: questionNumber,
        filename:
          normalizeFilename(row.supplemental_image_2) ||
          row.supplemental_image_2,
        image_type: "Contextual",
      });
    }
  });
  return imageMap;
}

export function matchImageFiles(questions, filesByName) {
  const usedKeys = new Set();
  const matchedQuestions = questions.map((row) => {
    const next = { ...row };
    [
      ["primary_image", "primary_image_matched"],
      ["supplemental_image_1", "supplemental_image_1_matched"],
      ["supplemental_image_2", "supplemental_image_2_matched"],
    ].forEach(([field, matchedField]) => {
      const key = normalizeFilename(row[field]);
      if (!key) {
        next[matchedField] = false;
        return;
      }
      const file = filesByName.get(key) || null;
      next[matchedField] = Boolean(file);
      if (file) usedKeys.add(key);
    });
    return next;
  });

  const unmatchedFiles = [];
  filesByName.forEach((file, key) => {
    if (!usedKeys.has(key) && file !== true) {
      unmatchedFiles.push(file.name || key);
    } else if (!usedKeys.has(key)) {
      unmatchedFiles.push(key);
    }
  });

  return { matchedQuestions, unmatchedFiles };
}

export function countMissingImages(questions) {
  return questions.reduce((count, row) => {
    let missing = 0;
    if (row.primary_image && !row.primary_image_matched) missing += 1;
    if (row.supplemental_image_1 && !row.supplemental_image_1_matched)
      missing += 1;
    if (row.supplemental_image_2 && !row.supplemental_image_2_matched)
      missing += 1;
    return count + missing;
  }, 0);
}

export async function listZipFilenames(file) {
  const buffer = await file.arrayBuffer();
  const view = new DataView(buffer);
  if (buffer.byteLength < 22) return [];

  let eocd = -1;
  const maxScan = Math.min(buffer.byteLength - 22, 65535);
  for (let i = 0; i <= maxScan; i += 1) {
    const offset = buffer.byteLength - 22 - i;
    if (view.getUint32(offset, true) === 0x06054b50) {
      eocd = offset;
      break;
    }
  }
  if (eocd < 0) return [];

  const cdSize = view.getUint32(eocd + 12, true);
  const cdOffset = view.getUint32(eocd + 16, true);
  const names = [];
  let offset = cdOffset;
  const cdEnd = Math.min(buffer.byteLength, cdOffset + cdSize);

  while (offset + 46 <= cdEnd) {
    if (view.getUint32(offset, true) !== 0x02014b50) break;
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const commentLen = view.getUint16(offset + 32, true);
    const nameBytes = new Uint8Array(buffer, offset + 46, nameLen);
    const name = new TextDecoder().decode(nameBytes);
    if (name && !name.endsWith("/")) names.push(name);
    offset += 46 + nameLen + extraLen + commentLen;
  }
  return names;
}

export function chunkItems(items, size = IMAGE_UPLOAD_CHUNK_SIZE) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export function chunkFilesByQuestion(
  imageMap,
  files,
  size = IMAGE_UPLOAD_CHUNK_SIZE
) {
  const filesByKey = new Map();
  (files || []).forEach((file) => {
    const key = normalizeFilename(file.name);
    if (key) filesByKey.set(key, file);
  });

  const grouped = new Map();
  const used = new Set();
  (imageMap || []).forEach((item) => {
    const key = normalizeFilename(item.filename);
    const file = filesByKey.get(key);
    if (!file || used.has(key)) return;
    used.add(key);
    const questionNumber = item.question_number;
    if (!grouped.has(questionNumber)) grouped.set(questionNumber, []);
    grouped.get(questionNumber).push(file);
  });

  const leftovers = (files || []).filter(
    (file) => !used.has(normalizeFilename(file.name))
  );
  const groups = Array.from(grouped.values());
  if (leftovers.length) groups.push(leftovers);

  const chunks = [];
  let current = [];
  groups.forEach((group) => {
    if (current.length && current.length + group.length > size) {
      chunks.push(current);
      current = [];
    }
    current.push(...group);
  });
  if (current.length) chunks.push(current);
  return chunks;
}

export function imageUploadHadProblems(results, imageMap = []) {
  if (!results) return false;
  if (results.missing_questions && results.missing_questions.length) {
    return true;
  }
  const attached = new Set(
    (results.attached || []).map(
      (item) =>
        `${item.question_number}:${normalizeFilename(item.filename)}:${
          item.image_type
        }`
    )
  );
  return (imageMap || []).some((item) => {
    const key = `${item.question_number}:${normalizeFilename(item.filename)}:${
      item.image_type
    }`;
    return !attached.has(key);
  });
}
